import React, { useState, useEffect } from "react";
import Loader from "components/loader";
import { useFormik } from "formik";
import * as yup from "yup";
import { toast } from "react-toastify";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { getUserById, updateAdmin } from "../../axios/api";

const generatePassword = () => {
  const minLength = 8;
  const lowercaseCharset = "abcdefghijklmnopqrstuvwxyz";
  const uppercaseCharset = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const numberCharset = "0123456789";
  const symbolCharset = '!@#$%^&*(),.?":{}|<>';

  let password = "";

  // Ensure at least one of each required character type
  password += lowercaseCharset.charAt(
    Math.floor(Math.random() * lowercaseCharset.length)
  );
  password += uppercaseCharset.charAt(
    Math.floor(Math.random() * uppercaseCharset.length)
  );
  password += numberCharset.charAt(
    Math.floor(Math.random() * numberCharset.length)
  );
  password += symbolCharset.charAt(
    Math.floor(Math.random() * symbolCharset.length)
  );

  // Fill the rest of the password with random characters from all charsets
  const remainingLength = minLength - password.length;
  const charset =
    lowercaseCharset + uppercaseCharset + numberCharset + symbolCharset;
  for (let i = 0; i < remainingLength; i++) {
    password += charset.charAt(Math.floor(Math.random() * charset.length));
  }

  // Shuffle the password to randomize character positions
  password = password
    .split("")
    .sort(() => Math.random() - 0.5)
    .join("");
  return password;
};

const UpdateAdmin = () => {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const id = location?.state;

  const [loading, setLoading] = useState(false);
  const [adminDetails, setAdminDetails] = useState(null);
  const [unChangedEmail, setUnChangedEmail] = useState("");

  const formik = useFormik({
    initialValues: {
      email: "",
      name: "",
      default_password_hash: "",
    },
    validationSchema: yup.object({
      email: yup.string(),
      name: yup.string(),
      default_password_hash: yup
        .string()
        .min(8, "Password must be at least 8 characters long")
        .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
        .matches(/[a-z]/, "Password must contain at least one lowercase letter")
        .matches(/[0-9]/, "Password must contain at least one number")
        .matches(
          /[!@#$%^&*(),.?":{}|<>]/,
          "Password must contain at least one special character"
        ),
    }),
    onSubmit: async (values) => {
      try {
        setLoading(true);
        if (
          unChangedEmail !== values?.email &&
          !values?.default_password_hash
        ) {
          toast.error("Please Generate Password too");
          return;
        }
        let response = await updateAdmin(id, {
          email: values?.email,
          name: values?.name,
          default_password_hash: values?.default_password_hash,
        });
        if (response?.data?.success) {
          toast.success("Admin Updated Successfully");
          navigate("/admin/all-admins");
        }
      } catch (error) {
        toast.error("Failed to update admin details");
      } finally {
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    const fetchAdminDetails = async () => {
      try {
        setLoading(true);
        const response = await getUserById(id);
        // console.log(response);
        if (response?.data?.success) {
          setAdminDetails(response?.data?.success);
          setUnChangedEmail(response?.data?.data?.user?.email);

          formik.setValues({
            name: response?.data?.data?.user?.name,
            email: response?.data?.data?.user?.email,
            default_password_hash: "",
          });
        }
      } catch (error) {
        console.error("Error fetching admin details:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAdminDetails();
  }, []);

  const handleGeneratePassword = () => {
    const newPassword = generatePassword();
    formik.setFieldValue("default_password_hash", newPassword);
  };

  return (
    <>
      <div className="m-3 bg-[#FFFFFF] p-3">
        {loading ? (
          <Loader />
        ) : (
          <form onSubmit={formik.handleSubmit}>
            <div className="my-5 flex w-11/12 flex-col">
              <div className="my-2 flex w-[61%] flex-col items-start justify-center gap-2">
                <label htmlFor="email" className="text-xl font-medium">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  id="email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className="h-10 w-[100%] rounded-md border-[2px] border-[#E4E6EF] bg-[transparent] p-2"
                />
                {formik.touched.email && formik.errors.email ? (
                  <div className="text-lg text-red-500">
                    {formik.errors.email}
                  </div>
                ) : null}

                <label htmlFor="email" className="text-xl font-medium">
                  UserName
                </label>
                <input
                  type="text"
                  name="name"
                  id="name"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="Enter Admin Username here"
                  className="h-10 w-[100%] rounded-md border-[2px] border-[#E4E6EF] bg-[transparent] p-2"
                />
                {formik.touched.name && formik.errors.name ? (
                  <div className="text-lg text-red-500">
                    {formik.errors.name}
                  </div>
                ) : null}

                <label
                  htmlFor="default_password_hash"
                  className="text-xl font-medium"
                >
                  Set New Password
                </label>
                <div className="flex w-full">
                  <input
                    type="text"
                    name="default_password_hash"
                    id="default_password_hash"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik?.values?.default_password_hash}
                    placeholder="Enter Admin Password here"
                    className="h-10 w-[100%] rounded-md border-[2px] border-[#E4E6EF] bg-[transparent] p-2"
                    disabled
                  />
                  <button
                    type="button"
                    onClick={handleGeneratePassword}
                    className="ml-2 rounded bg-[#008BD2] p-2 text-xl font-bold text-white"
                  >
                    Generate
                  </button>
                </div>
                {formik?.touched?.default_password_hash &&
                formik?.errors?.default_password_hash ? (
                  <div className="text-lg text-red-500">
                    {formik?.errors?.default_password_hash}
                  </div>
                ) : null}

                <div>
                  <button
                    name="submit"
                    className="float-right my-5 w-24 rounded bg-[#008BD2] p-2 text-xl font-bold text-white"
                    type="submit"
                  >
                    Submit
                  </button>
                </div>
              </div>
            </div>
          </form>
        )}
      </div>
    </>
  );
};

export default UpdateAdmin;
